import { useState } from 'react';
import type { Issue } from '../api';
import { HONEYPOT_FIELD } from '../honeypot';

export interface PlaceFields {
  name: string;
  category: string;
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  postal_code: string;
  description: string;
  website_url: string;
  phone: string;
  contact_email: string;
  credit_name: string;
}

export const EMPTY_PLACE_FORM: PlaceFields = {
  name: '',
  category: '',
  address_1: '',
  address_2: '',
  city: '',
  state: '',
  postal_code: '',
  description: '',
  website_url: '',
  phone: '',
  contact_email: '',
  credit_name: '',
};

const errFor = (issues: Issue[], path: string) => issues.find((i) => i.path === path)?.message;

export function PlaceForm(props: {
  initial: PlaceFields;
  issues: Issue[];
  busy: boolean;
  onSubmit: (fields: PlaceFields & { fax_number: string }) => void;
  extra?: React.ReactNode;
}) {
  const [f, setF] = useState(props.initial);
  const [fax, setFax] = useState(''); // honeypot
  const [creditOptIn, setCreditOptIn] = useState(Boolean(props.initial.credit_name));
  const set = <K extends keyof PlaceFields>(k: K, v: PlaceFields[K]) =>
    setF((s) => ({ ...s, [k]: v }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        props.onSubmit({ ...f, credit_name: creditOptIn ? f.credit_name : '', [HONEYPOT_FIELD]: fax });
      }}
    >
      <label htmlFor="pl-name">Business name</label>
      <input id="pl-name" required value={f.name} onChange={(e) => set('name', e.target.value)} />
      {errFor(props.issues, 'name') && <p className="error">{errFor(props.issues, 'name')}</p>}

      <label htmlFor="pl-category">Category</label>
      <input
        id="pl-category"
        list="pl-category-suggestions"
        required
        placeholder="e.g. Antique shop, Farm stand, Bakery"
        value={f.category}
        onChange={(e) => set('category', e.target.value)}
      />
      <datalist id="pl-category-suggestions">
        <option value="Antique shop" />
        <option value="Thrift store" />
        <option value="Consignment shop" />
        <option value="Farm stand" />
        <option value="Bakery" />
        <option value="Bookstore" />
        <option value="Flea market" />
      </datalist>
      {errFor(props.issues, 'category') && (
        <p className="error">{errFor(props.issues, 'category')}</p>
      )}

      <label htmlFor="pl-addr1">Street address</label>
      <input
        id="pl-addr1"
        required
        value={f.address_1}
        onChange={(e) => set('address_1', e.target.value)}
      />
      {errFor(props.issues, 'address_1') && (
        <p className="error">{errFor(props.issues, 'address_1')}</p>
      )}

      <label htmlFor="pl-addr2">Suite / unit (optional)</label>
      <input id="pl-addr2" value={f.address_2} onChange={(e) => set('address_2', e.target.value)} />

      <div className="row3">
        <div>
          <label htmlFor="pl-city">City</label>
          <input
            id="pl-city"
            required
            value={f.city}
            onChange={(e) => set('city', e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="pl-state">State</label>
          <input
            id="pl-state"
            required
            maxLength={2}
            value={f.state}
            onChange={(e) => set('state', e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="pl-zip">ZIP</label>
          <input
            id="pl-zip"
            required
            value={f.postal_code}
            onChange={(e) => set('postal_code', e.target.value)}
          />
        </div>
      </div>

      <label htmlFor="pl-desc">Description (optional)</label>
      <textarea
        id="pl-desc"
        value={f.description}
        onChange={(e) => set('description', e.target.value)}
      />

      <label htmlFor="pl-website">Website (optional)</label>
      <input
        id="pl-website"
        value={f.website_url}
        onChange={(e) => set('website_url', e.target.value)}
      />
      {errFor(props.issues, 'website_url') && (
        <p className="error">{errFor(props.issues, 'website_url')}</p>
      )}

      <label htmlFor="pl-phone">Phone (optional, shown publicly)</label>
      <input id="pl-phone" value={f.phone} onChange={(e) => set('phone', e.target.value)} />

      <label htmlFor="pl-contact-email">Your email (optional — we'll send a confirmation)</label>
      <input
        id="pl-contact-email"
        type="email"
        value={f.contact_email}
        onChange={(e) => set('contact_email', e.target.value)}
      />
      {errFor(props.issues, 'contact_email') && (
        <p className="error">{errFor(props.issues, 'contact_email')}</p>
      )}

      <label className="check">
        <input
          type="checkbox"
          checked={creditOptIn}
          onChange={(e) => setCreditOptIn(e.target.checked)}
        />
        Tag my name or business as who added this listing
      </label>
      {creditOptIn && (
        <>
          <label htmlFor="pl-credit">Name or business to show ("Added by ...")</label>
          <input
            id="pl-credit"
            required
            value={f.credit_name}
            onChange={(e) => set('credit_name', e.target.value)}
            maxLength={150}
          />
        </>
      )}

      {/* honeypot — hidden from people, irresistible to bots */}
      <div
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}
      >
        <label htmlFor="pl-fax-hp">Fax number</label>
        <input
          id="pl-fax-hp"
          name={HONEYPOT_FIELD}
          tabIndex={-1}
          autoComplete="off"
          value={fax}
          onChange={(e) => setFax(e.target.value)}
        />
      </div>

      {props.extra}
      <button className="primary" type="submit" disabled={props.busy}>
        Add this business
      </button>
    </form>
  );
}
